import { useMemo } from 'react';

import { useUsers } from '@/portainer/users/queries';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { TypedPolicy } from '../../../types';

import {
  KubernetesRbacFormData,
  KubernetesRbacPolicyPayload,
  KUBERNETES_RBAC_INITIAL_VALUES,
  KubernetesUnifiedAccess,
} from './types';

/**
 * Type guard to check if data is KubernetesRbacPolicyPayload
 */
function isKubernetesRbacPolicyPayload(
  data: unknown
): data is KubernetesRbacPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'EnvironmentAccessPolicies' in data
  );
}

/**
 * Hook to get initial values for Kubernetes RBAC Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useKubernetesRBACInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesRbacFormData {
  const usersQuery = useUsers(false);
  const users = useMemo(() => usersQuery.data || [], [usersQuery.data]);

  const teamsQuery = useTeams();
  const teams = useMemo(() => teamsQuery.data || [], [teamsQuery.data]);

  const rbacRolesQuery = useRbacRoles({
    select: (roles) =>
      roles.filter((role) => role.Scope && role.Scope.kubernetes),
  });
  const roles = useMemo(() => rbacRolesQuery.data || [], [rbacRolesQuery.data]);

  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_RBAC_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesRbacPolicyPayload(data)) {
      return KUBERNETES_RBAC_INITIAL_VALUES;
    }

    const accessList: KubernetesUnifiedAccess[] = [];

    // Extract environment access policies
    const envPolicies = data.EnvironmentAccessPolicies;
    if (envPolicies) {
      // Process user access policies
      const { UserAccessPolicies } = envPolicies;
      if (UserAccessPolicies) {
        Object.entries(UserAccessPolicies).forEach(([userId, policyData]) => {
          const id = parseInt(userId, 10);
          const user = users.find((u) => u.Id === id);
          if (user) {
            const roleId = policyData.value?.RoleId ?? 0;
            const roleName = roles.find((r) => r.Id === roleId)?.Name || '';
            accessList.push({
              Id: id,
              Type: 'user',
              Name: user.Username,
              Role: { Id: roleId, Name: roleName },
              namespaces: policyData.value?.Namespaces,
            });
          }
        });
      }

      // Process team access policies
      const { TeamAccessPolicies } = envPolicies;
      if (TeamAccessPolicies) {
        Object.entries(TeamAccessPolicies).forEach(([teamId, policyData]) => {
          const id = parseInt(teamId, 10);
          const team = teams.find((t) => t.Id === id);
          if (team) {
            const roleId = policyData.value?.RoleId ?? 0;
            const roleName = roles.find((r) => r.Id === roleId)?.Name || '';
            accessList.push({
              Id: id,
              Type: 'team',
              Name: team.Name,
              Role: { Id: roleId, Name: roleName },
              namespaces: policyData.value?.Namespaces,
            });
          }
        });
      }
    }

    return {
      accessList,
    };
  }, [policy, users, teams, roles]);
}
